// The module registry: maps a declarative HomeModule to its section component,
// pulling resolved data from the BrandContext. Add a new module by adding a case
// here + a variant in moduleTypes. Both the Module Library and the live home
// render through this one function.
import React from "react";

import { type BrandContext } from "./brandContext";
import { type HomeModule, type ProductSelector } from "./moduleTypes";
import { type ProductCardData } from "./ProductCard";
import { AboutModule } from "./sections/AboutModule";
import { BestsellersRow } from "./sections/BestsellersRow";
import { BetterTogetherModule } from "./sections/BetterTogetherModule";
import { BrandReviews } from "./sections/BrandReviews";
import { BundleModule } from "./sections/BundleModule";
import { CategoryTiles } from "./sections/CategoryTiles";
import { CollectionsRow } from "./sections/CollectionsRow";
import { EditorialBanner } from "./sections/EditorialBanner";
import { FaireMarketBanner } from "./sections/FaireMarketBanner";
import {
  type CollectionOverlayTreatment,
  FeaturedCollection,
} from "./sections/FeaturedCollection";
import { FeaturedReviews } from "./sections/FeaturedReviews";
import { HighlyRated } from "./sections/HighlyRated";
import { Masthead } from "./sections/Masthead";
import { ReferralBanner } from "./sections/ReferralBanner";
import { ReviewSpotlight } from "./sections/ReviewSpotlight";
import { VideoModule } from "./sections/VideoModule";

const selectCards = (
  select: ProductSelector,
  ctx: BrandContext
): ProductCardData[] => {
  if (typeof select === "string") return ctx.cards[select] ?? [];
  if ("categories" in select) {
    // One per category, de-duped: a category can be a keyword subset of
    // another (Boots and "Flats & Sandals" both match a bootie sandal), and
    // the same card turning up twice in a five-item sampler is conspicuous.
    const picked: ProductCardData[] = [];
    select.categories.forEach((label) => {
      const card = ctx
        .cardsByCategory(label)
        .find((entry) => !picked.some((seen) => seen.token === entry.token));
      if (card) picked.push(card);
    });
    return picked;
  }
  return ctx.cardsByCategory(select.category);
};

/** Render one module for a brand. `onShopAll` / `onSeeAll` let the host page wire
 * navigation (e.g. jump to the All Products tab); omit for a static preview. */
export function renderModule(
  module: HomeModule,
  ctx: BrandContext,
  handlers: {
    onShopAll?: () => void;
    onSeeAllVideos?: () => void;
    onProductClick?: (productToken: string) => void;
    /**
     * When set, product tiles become links to `productHref(token)` instead of
     * sign-up gates. For a surface that has somewhere deeper to send a
     * logged-out visitor (e.g. a PDP) rather than converting on every tile.
     */
    productHref?: (productToken: string) => string;
    /**
     * Showcase tuning for the brand.com surface (2026-08-14 review). The same
     * modules render on every surface; this is how a host page asks for the
     * looser, more editorial presentation without a parallel component set.
     * Omit entirely and every surface behaves exactly as it did before.
     */
    presentation?: {
      /** Show every Nth product tile as its lifestyle photo. */
      lifestyleEvery?: number;
      /** Drop names/prices under home tiles. */
      hideTileDetails?: boolean;
      /** About module: drop the social pair under the brand tags instead of
       * opposite the avatar. */
      aboutSocialBelowTags?: boolean;
      /** About module: hide the "Learn more about brand tags" link. */
      hideBrandTagsLink?: boolean;
      /** Highly rated: hide the product name under the reviewer. */
      hideHighlyRatedProductName?: boolean;
      /** Collection: how the collection name is made legible against the cover
       * photo. See `CollectionOverlayTreatment`. */
      collectionOverlayTreatment?: CollectionOverlayTreatment;
      /** Collection: stacked (below 1024) product layout. Desktop is unchanged. */
      collectionStackedProducts?: "grid" | "carousel";
      /** Collection: stacked (below 1024) cover crop. Desktop is unchanged. */
      collectionStackedCoverAspect?: "default" | "square";
    };
  } = {}
): React.ReactNode {
  switch (module.kind) {
    case "header":
      return (
        <Masthead
          brand={ctx.dataset}
          variant={module.variant}
          isLoggedOut={ctx.isLoggedOut}
        />
      );
    case "categories":
      return (
        <CategoryTiles
          tiles={ctx.dataset.categoryTiles}
          pickImage={ctx.pickImage}
          shopAllBackgroundColor={ctx.dataset.brandColor}
        />
      );
    case "collection": {
      const products = selectCards(module.select, ctx);
      return (
        <FeaturedCollection
          title={module.title}
          subtitle={module.subtitle}
          imageUrl={module.imageUrl ?? products[0]?.imageUrl}
          products={products}
          priceMode={ctx.priceMode}
          linkFor={
            handlers.productHref
              ? (card: ProductCardData) =>
                  handlers.productHref?.(card.token) ?? ""
              : undefined
          }
          aspectRatio={ctx.dataset.productImageAspect}
          backgroundColor={ctx.dataset.collectionBackgroundColor}
          hideTitleOverlay={module.hideTitleOverlay}
          overlayTreatment={handlers.presentation?.collectionOverlayTreatment}
          coverAspect={module.coverAspect}
          tileImageAspect={module.tileImageAspect}
          lifestyleEvery={handlers.presentation?.lifestyleEvery}
          hideDetails={handlers.presentation?.hideTileDetails}
          stackedProducts={handlers.presentation?.collectionStackedProducts}
          stackedCoverAspect={
            handlers.presentation?.collectionStackedCoverAspect
          }
        />
      );
    }
    case "productRow":
      return (
        <BestsellersRow
          title={module.title}
          products={selectCards(module.select, ctx)}
          priceMode={ctx.priceMode}
          rows={module.rows ?? 2}
          linkFor={
            handlers.productHref
              ? (card: ProductCardData) =>
                  handlers.productHref?.(card.token) ?? ""
              : undefined
          }
          showArrows={false}
          seeAllLabel={module.seeAllLabel ?? "Shop all products"}
          seeAllVariant="button"
          seeAllPlacement={module.seeAllPlacement}
          columns={module.columns}
          onSeeAll={handlers.onShopAll}
          aspectRatio={ctx.dataset.productImageAspect}
          lifestyleEvery={handlers.presentation?.lifestyleEvery}
          hideDetails={handlers.presentation?.hideTileDetails}
        />
      );
    case "productCarousel":
      return (
        <BestsellersRow
          title={module.title}
          products={selectCards(module.select, ctx)}
          priceMode={ctx.priceMode}
          aspectRatio={ctx.dataset.productImageAspect}
          lifestyleEvery={handlers.presentation?.lifestyleEvery}
          hideDetails={handlers.presentation?.hideTileDetails}
        />
      );
    case "bundle":
      return (
        <BundleModule
          title={module.title}
          description={module.description}
          products={selectCards(module.select, ctx)}
          priceMode={ctx.priceMode}
          ctaLabel={module.ctaLabel}
          aspectRatio={ctx.dataset.productImageAspect}
        />
      );
    case "betterTogether":
      return (
        <BetterTogetherModule
          title={module.title}
          description={module.description}
          products={ctx.cards.betterTogether ?? []}
          priceMode={ctx.priceMode}
        />
      );
    case "collectionsRow":
      return (
        <CollectionsRow
          collections={ctx.dataset.collections}
          columns={module.columns}
          title={module.title}
          imageAspect={module.imageAspect}
          onSeeAll={handlers.onShopAll}
        />
      );
    case "editorial":
      return (
        <EditorialBanner
          imageUrl={
            ctx.dataset.topCategoryHeroImage ??
            ctx.cards.topCategory?.[0]?.imageUrl
          }
          lineOne={ctx.dataset.name}
          lineTwo={ctx.dataset.topCategorySubtitle}
        />
      );
    case "faireMarket":
      return <FaireMarketBanner />;
    case "referral":
      return ctx.isLoggedOut ? <ReferralBanner /> : null;
    case "highlyRated":
      return (
        <HighlyRated
          products={ctx.cards.highlyRatedProducts}
          onShopAll={handlers.onShopAll}
          aspectRatio={ctx.dataset.productImageAspect}
          hideProductName={handlers.presentation?.hideHighlyRatedProductName}
        />
      );
    case "featuredReviews":
      return (
        <FeaturedReviews
          reviews={ctx.featuredReviews}
          title={module.title ?? ctx.dataset.featuredReviewsTitle}
          maxCount={module.count}
          layout={module.layout}
          imageUrl={ctx.dataset.featuredReviewsImage}
          showPurchased={module.showPurchased}
          onProductClick={handlers.onProductClick}
          onSeeAll={handlers.onShopAll}
        />
      );
    case "reviewSpotlight": {
      // Resolve "what they bought" from the review's own product tokens against
      // the brand's catalog. Brand-level reviews often carry none, which the
      // section handles by simply not rendering thumbnails.
      const items = ctx.featuredReviews
        .slice(0, module.count ?? 3)
        .map((review) => {
          const tokens = [
            ...(review.purchasedProductTokens ?? []),
            ...(review.productToken ? [review.productToken] : []),
          ];
          const productImageUrls = Array.from(new Set(tokens))
            .map(
              (token) =>
                ctx.allProducts.find((product) => product.token === token)
                  ?.imageUrl
            )
            .filter((url): url is string => Boolean(url));
          return {
            quote: review.reviewText,
            reviewerName: review.reviewerName,
            reviewerLocation: review.reviewerLocation,
            stars: review.stars,
            productImageUrls:
              productImageUrls.length > 0
                ? productImageUrls
                : (review.purchasedProductImageUrls ?? []),
          };
        });
      return <ReviewSpotlight reviews={items} title={module.title} />;
    }
    case "reviews":
      return ctx.dataset.rating ? (
        <BrandReviews
          rating={ctx.dataset.rating}
          reviewCount={ctx.dataset.reviewCount ?? 0}
          distribution={ctx.dataset.reviewDistribution}
          products={ctx.cards.reviewProducts}
          brandReviews={ctx.brandReviews}
        />
      ) : null;
    case "video":
      return (
        <VideoModule
          videos={ctx.dataset.videos}
          onSeeAll={handlers.onSeeAllVideos}
        />
      );
    case "about":
      return (
        <AboutModule
          brand={ctx.dataset}
          socialPlacement={
            handlers.presentation?.aboutSocialBelowTags ? "belowTags" : "header"
          }
          hideBrandTagsLink={handlers.presentation?.hideBrandTagsLink}
        />
      );
    default:
      return null;
  }
}
